<script>
import { mapState, mapActions } from "vuex";
import { get, keys } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { loginWithGoogle } from "@/services/socialLogin";
import { finishSocialTalentSession } from "@/services/finishSocialTalentSession";
import SocialLoginRegistrationModal from "@/components/form/SocialLoginRegistrationModal.vue";
import SocialLoginLinkAccountModal from "@/components/form/SocialLoginLinkAccountModal.vue";
import { sanitizeQuery } from "@/utils/utmStorage";

export default {
  name: "redefine-password-form",
  components: {
    ValidationProvider,
    ValidationObserver,
    SocialLoginRegistrationModal,
    SocialLoginLinkAccountModal,
  },
  data: () => ({
    password: null,
    passwordConfirmation: null,
    passwordStrength: [],
    strength: null,
    showPassword: false,
    redefined: false,
    invalidCode: false,
    socialRegModalActive: false,
    socialLinkModalActive: false,
    pendingSocialToken: null,
    pendingMaskedEmail: "",
  }),
  computed: {
    ...mapState("company", ["company"]),
    logo() {
      return get(this.$store, "state.company.company.logo_url", null);
    },
    title() {
      if (this.invalidCode) return "Código inválido ou expirado";
      return this.redefined ? "Senha redefinida" : "Defina uma nova senha";
    },
    subtitle() {
      if (this.invalidCode) {
        return "O link de redefinição de senha é inválido ou já expirou. Por favor, solicite um novo link de recuperação.";
      }
      return this.redefined
        ? "Sua senha foi redefinida com sucesso, clique no botão para fazer login."
        : "Ela deve ser diferente da senha anterior";
    },
    isAnonimous() {
      return (
        get(this.$router, "history.current.params.company_label", null) === "anonima"
      );
    },
    passwordsAreEqual() {
      return (
        this.password !== null &&
        this.passwordConfirmation !== null &&
        this.password === this.passwordConfirmation
      );
    },
    shouldShowSocialLoginNotice() {
      const socialLoginParam = get(
        this.$route,
        "query.is-social-login",
        get(this.$route, "query.is_social_login", null)
      );

      return (
        socialLoginParam === "1" ||
        socialLoginParam === 1 ||
        socialLoginParam === true ||
        socialLoginParam === "true"
      );
    },
  },
  methods: {
    ...mapActions("step", ["updateCurrentStep"]),
    async onSocialGoogleCompleted(payload) {
      const { utm_source } = sanitizeQuery(this.$router.history.current.query);
      const { vacancy_id: routeVacancy, company_label: routeLabel } =
        this.$router.history.current.params;
      const company =
        this.company ||
        (this.$store.state.company && this.$store.state.company.company) ||
        null;
      const vacancyId =
        payload.vacancy_id != null ? payload.vacancy_id : routeVacancy;
      const companyLabel =
        payload.company_label != null ? payload.company_label : routeLabel;
      this.$vs.loading({ text: "Entrando..." });
      try {
        await finishSocialTalentSession(
          this.$store,
          this.$router,
          this.updateCurrentStep,
          {
            token: payload.token,
            created_account: payload.created_account,
            application_id: payload.application_id,
            vacancy_id: vacancyId,
            company_label: companyLabel,
            company,
            utm_source,
          }
        );
      } catch (e) {
        this.$vs.notify({
          color: "danger",
          title: "Login com Google",
          text: "Não foi possível concluir o acesso. Tente novamente.",
          time: 12000,
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async handleGoogleLogin() {
      this.$vs.loading({ text: "Abrindo login com Google..." });
      const { vacancy_id, company_label } = this.$router.history.current.params;
      const { utm_source, utm_campaign, utm_medium } = sanitizeQuery(this.$router.history.current.query);
      try {
        const result = await loginWithGoogle({
          company_label: company_label || undefined,
          vacancy_id: vacancy_id || undefined,
          channel: utm_source || undefined,
          utm_campaign: utm_campaign || undefined,
          utm_medium: utm_medium || undefined,
        });

        if (result.action === "needs_registration") {
          this.pendingSocialToken = result.social_token;
          this.$vs.loading.close();
          this.socialRegModalActive = true;
          return;
        }
        if (result.action === "needs_password") {
          this.pendingSocialToken = result.social_token;
          this.pendingMaskedEmail = result.user_email || "";
          this.$vs.loading.close();
          this.socialLinkModalActive = true;
          return;
        }

        const company =
          this.company ||
          (this.$store.state.company && this.$store.state.company.company) ||
          null;
        await finishSocialTalentSession(
          this.$store,
          this.$router,
          this.updateCurrentStep,
          {
            token: result.token,
            created_account: result.created_account,
            application_id: result.application_id,
            vacancy_id,
            company_label,
            company,
            utm_source,
          }
        );
        this.$vs.loading.close();
      } catch (err) {
        this.$vs.loading.close();
        const msg =
          err && err.error === "wrong_account_type"
            ? "Área de login somente para o candidato. Faça login com uma conta diferente ou clique  em Criar uma conta."
            : err && err.error === "social_auth_failed"
              ? "Não foi possível concluir o login com Google. Tente novamente."
              : err && err.error === "google_account_mismatch"
                ? "Esta conta Google não corresponde ao login já vinculado à sua conta."
                : err && err.error === "social_email_required"
                  ? "Sua conta Google não retornou e-mail. Use outra conta ou cadastre-se com e-mail."
              : err && err.error === "popup_closed"
                ? err.message
                : err && err.error === "popup_blocked"
                  ? err.message
              : err && err.message
                ? err.message
                : "Login com Google cancelado ou falhou.";

        this.$vs.notify({
          color: "danger",
          title: "Login com Google",
          text: msg,
          time: 12000,
        });
      }
    },
    shouldShowCompanyLogo() {
      return keys(this.company).length > 0;
    },
    async handleRedefine() {
      try {
        if (this.password == this.passwordConfirmation) {
          this.$vs.loading({
            text: "Salvando...",
          });

          const payload = {
            password: this.password,
            password_confirmation: this.passwordConfirmation,
          };

          const code = get(this.$router, "history.current.params.code", null);

          if (!code) return;
          
          const response = await this.$http.post(
            `api/v1/reset/password/${code}`,
            payload
          );

          if (response.status == 200) {
            this.$vs.loading.close();

            localStorage.setItem("last_login_method", "password");

            this.redefined = true;

            this.$vs.notify({
              title: "Parabéns!",
              text: "Sua senha foi alterada com sucesso",
              iconPack: "feather",
              icon: "icon-alert-circle",
              color: "success",
            });
          }

          if (response.status == 400) {
            this.$vs.loading.close();
          }
        } else {
          this.$vs.notify({
            title: "Verifique as senhas",
            text: "As senhas informadas não são iguais",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "warning",
          });
        }
      } catch (error) {
        this.$vs.loading.close();
        
        const errorMessage = get(error, 'response.data.message', '');
        const statusCode = get(error, 'response.status', null);
        
        // Verifica se é erro de código inválido/expirado
        if (statusCode === 400 && (errorMessage.toLowerCase().includes('inválido') || errorMessage.toLowerCase().includes('invalido') || errorMessage.toLowerCase().includes('expirado'))) {
          this.invalidCode = true;
          return;
        }
        
        this.$vs.notify({
          title: "Falha ao resetar a senha",
          text: errorMessage || error.message || "Falha ao realizar o procedimento contate a nossa equipe de suporte",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
        });
      }
    },
    handleRecoverAgain() {
      const { company_label } = this.$router.history.current.params;
      this.$router.push({
        name: 'forgot-password',
        params: { company_label }
      });
    },
  },
  watch: {
    password: function (val) {
      function checkPasswordStrength(password) {
        const minLength = 8;
        let passwordStrengthStages = [];

        function hasMinLength() {
          return password.length >= minLength;
        }

        function hasCharsInSequence() {
          const regex = /(abcdefghijklmnopqrstuvwxyz)|(1234567890)|(qwertyuiop)|(asdfghjkl)|(zxcvbnm)/;
          return regex.test(password);
        }

        function hasLowerCase() {
          return /[a-z]/.test(password);
        }

        function hasOnlyLowerCase() {
          const regex = /^[a-z]+$/;
          return regex.test(password);
        }

        function hasUpperCase() {
          return /[A-Z]/.test(password);
        }

        function hasNumber() {
          return /[0-9]/.test(password);
        }

        function hasSpecialChars() {
          return /[!@#$%^&*(),.?":{}|<>]+/.test(password);
        }

        if (password.length < minLength) {
          return passwordStrengthStages;
        }

        if (hasMinLength() || hasCharsInSequence() || hasOnlyLowerCase()) {
          passwordStrengthStages = [...passwordStrengthStages, "Weak"];
        }

        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          !hasCharsInSequence() &&
          !hasNumber() &&
          !hasSpecialChars() &&
          !hasOnlyLowerCase()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
        }

        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          hasNumber() &&
          !hasSpecialChars() &&
          !hasCharsInSequence()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          if (!passwordStrengthStages.includes("Moderate")) {
            passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Strong"];
        }

        // Check conditions for "Very Strong"
        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          hasNumber() &&
          hasSpecialChars() &&
          !hasCharsInSequence() &&
          !hasOnlyLowerCase()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          if (!passwordStrengthStages.includes("Moderate")) {
            passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
          }

          if (!passwordStrengthStages.includes("Strong")) {
            passwordStrengthStages = [...passwordStrengthStages, "Strong"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Very Strong"];
        }

        return passwordStrengthStages;
      }

      function getBarColor(strength, index) {
        switch (strength) {
          case "Weak":
            return index === 0 ? "linear-gradient(to right, lightcoral, red)" : "#E0E0E0";
          case "Moderate":
            return index <= 1
              ? "linear-gradient(to right, lightyellow, yellow)"
              : "#E0E0E0";
          case "Strong":
            return index <= 2 ? "linear-gradient(to right, #FED8B1, orange)" : "#E0E0E0";
          case "Very Strong":
            return index <= 3
              ? "linear-gradient(to right, lightgreen, green)"
              : "#E0E0E0";
          default:
            return "#E0E0E0";
        }
      }

      const strengths = checkPasswordStrength(val);
      const bars = document.querySelectorAll(".bar");

      const strengthText = {
        Weak: "Fraca",
        Moderate: "Moderada",
        Strong: "Forte",
        "Very Strong": "Muito forte",
      };

      this.strength = strengthText[strengths[strengths.length - 1]];

      bars.forEach((bar, index) => {
        bar.style.background = getBarColor(strengths[index], index);
      });
    },
  },
  async created() {
    const label = get(this.$route, "params.company_label", null)
    if (label) {
      await this.$store.dispatch("company/fetchCompany", label)
    }
  },
};
</script>

<template>
  <div class="redefine-password-form">
    <img
      v-show="shouldShowCompanyLogo() && !isAnonimous"
      class="redefine-password-form__logo"
      alt="Company Logo"
      :src="logo"
    />
    <div v-if="invalidCode" class="invalid-code-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
    </div>
    <h2>{{title}}</h2>
    <p>{{subtitle}}</p>
    <div
      v-if="!redefined && !invalidCode && shouldShowSocialLoginNotice"
      class="redefine-password-form__google-notice"
    >
      <div class="redefine-password-form__google-unlink-alert-box" role="alert">
        <feather-icon
          icon="AlertCircleIcon"
          class="redefine-password-form__google-unlink-alert-box-icon"
        />
        <div class="redefine-password-form__google-unlink-alert-text">
          Atenção: se sua conta estiver vinculada a uma conta Google, ao salvar a nova senha esse vínculo poderá ser removido e você precisará acessar com e-mail e senha.
          <strong class="redefine-password-form__google-unlink-alert-em">Se quiser continuar usando o Google, clique no link abaixo</strong>
        </div>
      </div>
      <button
        type="button"
        class="redefine-password-form__google-btn"
        @click="handleGoogleLogin"
      >
        <span class="redefine-password-form__google-btn-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </span>
        Entrar com Google
      </button>
    </div>
    <ValidationObserver v-if="!redefined && !invalidCode" tag="form" ref="observer" @submit.prevent="handleRedefine()">
      <ValidationProvider
        vid="password"
        name="senha"
        rules="required|min:8"
        v-slot="{ errors }"
      >
        <vs-input
          class="redefine-password-form__input"
          icon="https"
          :type="!showPassword ? 'password' : 'text'"
          v-model="password"
          :style="{ zIndex: 0 }"
          :placeholder="$t('placeholders.password')"
          :label-placeholder="$t('placeholders.password')"
          :danger="Boolean(errors.length)"
          :danger-text="errors[0]"
        />
        <feather-icon
          :class="
            errors.length > 0
              ? 'redefine-password-form__password-eye-error'
              : 'redefine-password-form__password-eye'
          "
          :icon="!showPassword ? 'EyeOffIcon' : 'EyeIcon'"
          @click="() => (showPassword = !showPassword)"
        />
      </ValidationProvider>
      <div class="password-wrapper">
        <p class="password__tips">
          Use uma combinação de letras, números e caracteres especiais. Mínimo de 8
          caracteres.
        </p>
        <div class="password-strength-meter">
          <div class="bar"></div>
          <div class="bar"></div>
          <div class="bar"></div>
          <div class="bar"></div>
        </div>
        <span>{{ strength }}</span>
      </div>
      <ValidationProvider
        name="confirmar_senha"
        rules="required|confirmed:password"
        v-slot="{ errors }"
        :style="{ marginTop: '1rem' }"
      >
        <vs-input
          class="redefine-password-form__input"
          icon="https"
          :type="!showPassword ? 'password' : 'text'"
          v-model="passwordConfirmation"
          :style="{ zIndex: 0 }"
          placeholder="Confirmar senha"
          :label-placeholder="$t('placeholders.password')"
          :danger="Boolean(errors.length)"
          :danger-text="errors[0]"
        />
        <feather-icon
          :class="
            errors.length > 0
              ? 'redefine-password-form__password-eye-error'
              : 'redefine-password-form__password-eye'
          "
          :icon="!showPassword ? 'EyeOffIcon' : 'EyeIcon'"
          @click="() => (showPassword = !showPassword)"
        />
      </ValidationProvider>
      <button
        class="submit-btn bg-primary"
        type="submit"
        :disabled="!passwordsAreEqual"
        :style="{ opacity: passwordsAreEqual ? 1 : 0.5 }"
      >
        Redefinir senha
      </button>
    </ValidationObserver>
    <vs-button
      v-else-if="redefined"
      class="submit-btn bg-primary"
      @click="() => $router.push({ name: 'signin' })"
    >
      Fazer login
    </vs-button>
    <div v-if="invalidCode" class="invalid-code-actions">
      <vs-button
        class="submit-btn bg-primary"
        @click="handleRecoverAgain"
      >
        Recuperar senha novamente
      </vs-button>
      <vs-button
        type="flat"
        class="back-btn"
        icon="arrow_back_ios"
        @click="() => $router.push({ name: 'signin' })"
      >
        Voltar ao login
      </vs-button>
    </div>
    <SocialLoginRegistrationModal
      :active.sync="socialRegModalActive"
      :social-token="pendingSocialToken"
      @completed="onSocialGoogleCompleted"
    />
    <SocialLoginLinkAccountModal
      :active.sync="socialLinkModalActive"
      :social-token="pendingSocialToken"
      :user-email="pendingMaskedEmail"
      @completed="onSocialGoogleCompleted"
    />
  </div>
</template>

<style lang="scss" scoped>
.submit-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.85rem 2.5rem;
  color: white;
  font-size: 16px;
  font-weight: 200;
  border: 0px;
  border-radius: 5px;

  margin: 20px 0 20px 0;
  transition: color 0.5s;

  &:hover {
    color: #fcfcfc;
    cursor: pointer;
  }
}
.password-wrapper {
  margin-top: -1rem;
  margin-bottom: 2rem;

  & > span {
    color: #7a869a;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: 0.048px;
  }
}

.password-strength-meter {
  display: flex;
  flex-direction: row;
  margin: 10px 0;

  .bar {
    height: 4px;
    flex-grow: 1;
    margin: 0 5px;
    background-color: #e0e0e0;
  }
}
.redefine-password-form {
  width: 360px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 1rem;
  gap: 1rem;

  &__google-notice {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 0.5rem;
  }

  &__google-unlink-alert-box {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    margin: 0;
    padding: 12px;
    box-sizing: border-box;
    border-radius: 6px;
    background: rgba(255, 159, 67, 0.15);
    box-shadow: 0 0 25px 0 rgba(255, 159, 67, 0.15);
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  &__google-unlink-alert-box-icon {
    flex-shrink: 0;
    margin-top: 1px;

    svg {
      width: 20px;
      height: 20px;
      color: #991b1b;
    }
  }

  &__google-unlink-alert-em {
    font-weight: 700;
  }

  &__google-unlink-alert-text {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 400;
    color: #991b1b;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
  }

  &__google-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 0.85rem 2.5rem;
    background: #fff;
    color: #3c4043;
    border: 1px solid #dadce0;
    border-radius: 5px;
    font-size: 16px;
    font-weight: 200;
    cursor: pointer;
    margin: 0;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: #f8f9fa;
      color: #3c4043;
    }
  }

  &__google-btn-icon {
    display: flex;
    align-items: center;
  }

  h2 {
    align-self: stretch;
    color: #42526e;
    font-family: Roboto;
    font-size: 26px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.52px;
  }

  p {
    color: #42526e;
    font-family: Roboto;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.4px;
  }

  &__logo {
    max-width: 130px;
    max-height: 150px;
    margin: 0 auto;
    margin-bottom: 05px;
    margin-top: 10px;
    object-fit: contain;
  }

  &__input {
    width: 100%;
  }

  &__password-eye {
    position: absolute;
    top: -30px;
    right: -92%;
    transition-delay: 0s;
    svg {
      cursor: pointer;
      width: 18px;
      color: gray !important;
    }
  }

  &__password-eye-error {
    position: absolute;
    top: -48px;
    right: -90%;
    z-index: 100;
    transition-delay: 0s;
    svg {
      cursor: pointer;
      width: 18px;
      color: gray !important;
    }
  }
}

p.password__tips {
  color: #7a869a;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  letter-spacing: 0.28px;
  margin-bottom: 1rem;
}

.invalid-code-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.invalid-code-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.back-btn {
  color: #42526e;
  padding: 0;
  font-size: 14px;
}
</style>
